"use client";

import type { ActionButtonProps } from "@/components/ActionButton";
import BackgroundSvg from "@/components/BackgroundSvg";
import { Container } from "@/components/Container";
import { AdaptiveText, type AdaptiveTextProps } from "@/components/Content";
import { Title } from "@/components/Content/Title";
import { Sizes } from "@/components/Content/styles";
import { RadiantCard } from "@/components/RadiantCard";
import type { ImageDetails } from "@/types";
import { motion } from "framer-motion";
import { useRef } from "react";

export type SolutionCard = {
	title: string;
	text: AdaptiveTextProps["portableText"];
	image: ImageDetails;
	action: ActionButtonProps;
};

type Props = {
	id?: string;
	title?: string;
	text?: AdaptiveTextProps["portableText"];
	solutionCards: SolutionCard[];
};

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.2,
			delayChildren: 0.1,
		},
	},
};

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.6,
			ease: "easeOut" as const,
		},
	},
};

const cardContainerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.15,
			delayChildren: 0.3,
		},
	},
};

const cardVariants = {
	hidden: { opacity: 0, y: 30, scale: 0.95 },
	visible: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.7,
			ease: "easeOut" as const,
		},
	},
};

const Solutions = ({ id, title, text, solutionCards }: Props) => {
	const sectionRef = useRef(null);

	return (
		<section id={id} className="relative" ref={sectionRef}>
			<BackgroundSvg />
			<BackgroundSvg position="bottom-right" />
			<Container containerClassName="py-32" className="flex flex-col gap-16">
				<motion.div
					className="flex flex-col"
					variants={containerVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-50px" }}
				>
					<motion.div variants={itemVariants}>
						<Title as="h2" className="max-w-3xl">
							{title}
						</Title>
					</motion.div>
					<motion.div variants={itemVariants}>
						<AdaptiveText
							portableText={text}
							className="max-w-lg"
							size={Sizes.Section}
						/>
					</motion.div>
				</motion.div>
				<motion.div
					className="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3"
					variants={cardContainerVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-50px" }}
				>
					{solutionCards.map((solution) => (
						<motion.div key={solution.title} variants={cardVariants}>
							<RadiantCard
								title={solution.title}
								text={solution.text}
								image={solution.image}
								action={solution.action}
								className="h-full max-w-96 lg:max-w-none"
							/>
						</motion.div>
					))}
				</motion.div>
			</Container>
		</section>
	);
};

export default Solutions;
